import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminInputClass } from '@/components/admin/AdminTableStyles';
import { centsToDollarInput, formatCents, previewTotals } from '@/lib/accounting';
import { cn } from '@/lib/utils';

/**
 * The line-item grid shared by Create Invoice and Create Bill.
 *
 * One component, two contexts. The `items` it is given are already filtered by
 * the caller to the sellable or the buyable half of the catalog, which is why a
 * sell-only service cannot be picked onto a bill here — and the server refuses
 * it again regardless, because a filtered dropdown is not a validation.
 *
 * Picking a catalog item fills the description, price and taxes from the
 * catalog *once*. After that the line is the person's to edit: this is the
 * moment the values are snapshotted, and a later catalog price change must not
 * reach back into a document already written.
 */

export function emptyLine() {
  return { item_id: '', description: '', quantity: '1', unit_price: '0.00', tax_ids: [] };
}

export default function LineItemsEditor({ lines, onChange, items, taxes, disabled }) {
  const taxesById = Object.fromEntries((taxes || []).map((tax) => [tax.id, tax]));
  const totals = previewTotals(lines, taxesById);

  const setLine = (index, patch) =>
    onChange(lines.map((line, position) => (position === index ? { ...line, ...patch } : line)));

  const selectItem = (index, itemId) => {
    const item = (items || []).find((candidate) => candidate.id === itemId);
    if (!item) {
      setLine(index, { item_id: '', tax_ids: [] });
      return;
    }
    setLine(index, {
      item_id: item.id,
      description: item.name,
      unit_price: centsToDollarInput(item.price_cents),
      tax_ids: item.tax_ids || [],
    });
  };

  const toggleTax = (index, taxId) => {
    const current = lines[index]?.tax_ids || [];
    setLine(index, {
      tax_ids: current.includes(taxId)
        ? current.filter((value) => value !== taxId)
        : [...current, taxId],
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>Line items</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange([...lines, emptyLine()])}
        >
          <Plus className="mr-1 size-3.5" />
          Add line
        </Button>
      </div>

      <div className="space-y-3">
        {lines.map((line, index) => {
          const quantity = Number.parseFloat(line.quantity);
          const unitCents = Math.round(Number.parseFloat(String(line.unit_price || 0).replace(/[$,]/g, '')) * 100) || 0;
          const amount = Math.round((Number.isFinite(quantity) ? quantity : 1) * unitCents);
          return (
            <div
              key={index}
              className="rounded-lg border border-[#062B1A]/10 bg-[#F7F8FA]/60 p-3"
            >
              <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_90px_130px_auto]">
                <div className="space-y-2">
                  <select
                    aria-label={`Line ${index + 1} product or service`}
                    value={line.item_id}
                    disabled={disabled}
                    onChange={(event) => selectItem(index, event.target.value)}
                    className={cn(
                      'h-10 w-full rounded-md border bg-white px-3 text-sm',
                      adminInputClass
                    )}
                  >
                    <option value="">Custom line (no catalog item)</option>
                    {(items || []).map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name} — {item.price_display}
                      </option>
                    ))}
                  </select>
                  <Input
                    aria-label={`Line ${index + 1} description`}
                    value={line.description}
                    disabled={disabled}
                    onChange={(event) => setLine(index, { description: event.target.value })}
                    placeholder="Description"
                    className={adminInputClass}
                  />
                </div>
                <Input
                  aria-label={`Line ${index + 1} quantity`}
                  type="number"
                  step="0.01"
                  value={line.quantity}
                  disabled={disabled}
                  onChange={(event) => setLine(index, { quantity: event.target.value })}
                  className={adminInputClass}
                />
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-[#062B1A]/45">
                    C$
                  </span>
                  <Input
                    aria-label={`Line ${index + 1} unit price`}
                    type="number"
                    step="0.01"
                    value={line.unit_price}
                    disabled={disabled}
                    onChange={(event) => setLine(index, { unit_price: event.target.value })}
                    className={cn('pl-9', adminInputClass)}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={disabled || lines.length === 1}
                  aria-label={`Remove line ${index + 1}`}
                  onClick={() => onChange(lines.filter((_, position) => position !== index))}
                >
                  <Trash2 className="size-4 text-red-600" />
                </Button>
              </div>

              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  {(taxes || []).map((tax) => {
                    const on = (line.tax_ids || []).includes(tax.id);
                    return (
                      <button
                        key={tax.id}
                        type="button"
                        disabled={disabled}
                        onClick={() => toggleTax(index, tax.id)}
                        className={cn(
                          'rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors',
                          on
                            ? 'border-[#D4AF37] bg-[#062B1A] text-white'
                            : 'border-[#062B1A]/15 bg-white text-[#062B1A]/60 hover:bg-[#062B1A]/5'
                        )}
                      >
                        {tax.label}
                      </button>
                    );
                  })}
                  {!(taxes || []).length && (
                    <span className="text-[11px] text-[#062B1A]/40">No taxes configured</span>
                  )}
                </div>
                <span className="text-sm font-semibold text-[#062B1A]">{formatCents(amount)}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="ml-auto w-full max-w-xs space-y-1 rounded-lg border border-[#062B1A]/10 bg-white p-3 text-sm">
        <div className="flex justify-between text-[#062B1A]/60">
          <span>Subtotal</span>
          <span>{formatCents(totals.subtotalCents)}</span>
        </div>
        {totals.taxes.map((tax) => (
          <div key={tax.tax_id} className="flex justify-between text-[#062B1A]/60">
            <span>{tax.label}</span>
            <span>{formatCents(tax.amount_cents)}</span>
          </div>
        ))}
        <div className="flex justify-between border-t border-[#062B1A]/10 pt-1 font-semibold text-[#062B1A]">
          <span>Total</span>
          <span>{formatCents(totals.totalCents)}</span>
        </div>
      </div>
    </div>
  );
}
